import { useIsDesktop } from "@wingspanhq/fe-component-library";
import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Admin } from "../../Admin";
import { BookkeepingRoutes } from "../../Bookkeeping/index";
import { Data } from "../../Data";
import { Invoices } from "../../Invoices";
import { KnowledgeCenterRoot } from "../../KnowledgeCenter";
import { BeginEmailVerification } from "../../MemberOnboarding/screens/BeginEmailVerification";
import { CompleteEmailVerification } from "../../MemberOnboarding/screens/CompleteEmailVerification";
import { EmailMagicLinkHandler } from "../../MemberOnboarding/screens/EmailMagicLinkHandler";
import { OnboardingSetPassword } from "../../MemberOnboarding/screens/OnboardingSetPassword";
import { VerifyOTP } from "../../MemberOnboarding/screens/VerifyOTP";
import { VerifyPhoneNumber } from "../../MemberOnboarding/screens/VerifyPhoneNumber";
import { VerifyPhoneNumberExistingAccount } from "../../MemberOnboarding/screens/VerifyPhoneNumberExistingAccount";
import { OrganizationIndex } from "../../Organization";
import { PersonalPayLinkRoutes } from "../../PersonalPayLink";
import { AddNewToken } from "../../Settings/screens/AddNewToken";
import { EditNewToken } from "../../Settings/screens/EditNewToken";
import { MembershipBillingHistory } from "../../Settings/screens/MembershipBillingHistory";
import { MembershipPlansWithAddOns } from "../../Settings/screens/MembershipPlansWithAddOns";
import { MembershipTrialSuccess } from "../../Settings/screens/MembershipTrialSuccess";
import { NextGenSubscriptionPlans } from "../../Settings/screens/NextGenSubscriptionPlans";
import { SettingsMain as Settings } from "../../Settings/screens/SettingsMain";
import { NEC1099Filing } from "../../modules/1099NECFiling";
import { BenefitsRoutes } from "../../modules/Benefits";
import { BulkImportsIndex } from "../../modules/BulkImporter";
import { BulkReviewIndex } from "../../modules/BulkReview";
import { DashboardIndex } from "../../modules/Dashboard";
import { ENGAGEMENT_TYPES_ROOT_PATH } from "../../modules/EngagementTypes/paths";
import { RouteIndexEngagementTypes } from "../../modules/EngagementTypes/routes";
import { MyResourcesIndex } from "../../modules/MyResources";
import { PATH_MY_RESOURCES } from "../../modules/MyResources/paths";
import { Onboarding } from "../../modules/Onboarding";
import { PayeesIndex } from "../../modules/Payees";
import { PayersIndex } from "../../modules/Payers";
import { PayoutsIndex } from "../../modules/Payouts";
import { ReportsIndex } from "../../modules/Reports";
import { EngagementRequirementsIndex } from "../../modules/RequirementDefinitions";
import { StatementIndex } from "../../modules/Statements";
import { TaxDocuments } from "../../modules/TaxDocuments";
import { Tax1099Filing } from "../../modules/TaxFiling";
import { Taxes } from "../../modules/Taxes";
import { PATH_TAXES } from "../../modules/Taxes/paths";
import { Transfer } from "../../modules/Transfer";
import { Wallet } from "../../modules/Wallet";
import { RouteActivateBanking } from "../../modules/Wallet/routes/RouteActivateBanking";
import { WorkLogs } from "../../modules/WorkLogs";
import { PATH_WORK_LOGS } from "../../modules/WorkLogs/paths";
import { useQueryPaymentsStatus } from "../../query/payments/queries";
import { PaymentsSecurityScreen } from "../../screens/PaymentsSecurity";
import { GeneralErrorPage } from "../../shared/components/GeneralErrorPage";
import { ModalPayoutAccounts } from "../../shared/components/ModalPayoutAccounts";
import { MemberPlaidModals } from "../../utils/usePlaidLink";
import { BaseAppLayout } from "../BaseAppLayout/BaseAppLayout.component";
import { LoadingPlaceholder } from "../LoadingPlaceholder";
import { OptVerification } from "../OptVerification";
import { useInitMemberApp } from "./useInitMemberApp";
import { ENGAGEMENT_REQUIREMENTS_ROOT_PATH } from "../../modules/RequirementDefinitions/paths/constants";
import { SystemLog } from "./SystemLog";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import { PATH_TRANSFER } from "../../modules/Transfer/paths";

export const MemberApp: React.FC = () => {
  const isDesktop = useIsDesktop();
  const queryFeatureFlags = useFeatureFlags();

  const [initMemberApp, initMemberAppMeta] = useInitMemberApp();

  useEffect(() => {
    initMemberApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-flight
  useQueryPaymentsStatus({
    enabled: !initMemberAppMeta.isLoading && !initMemberAppMeta.isIdle
  });

  // Store intercomUserJwtAuth experiment flag globally for use in analytics
  useEffect(() => {
    if (queryFeatureFlags.data?.intercomUserJwtAuth !== undefined) {
      window.intercomUserJwtAuthExperiment =
        queryFeatureFlags.data.intercomUserJwtAuth;
    }
  }, [queryFeatureFlags.data?.intercomUserJwtAuth]);

  if (
    initMemberAppMeta.isLoading ||
    initMemberAppMeta.isIdle ||
    !queryFeatureFlags.isFetched
  ) {
    return <LoadingPlaceholder />;
  } else if (initMemberAppMeta.isError) {
    return <GeneralErrorPage />;
  }

  return (
    <React.Fragment>
      <SystemLog />
      <OptVerification />
      <MemberPlaidModals />
      <ModalPayoutAccounts />
      <Switch>
        {/* Fullscreen page routes */}

        <Route path="/member/onboarding" component={Onboarding} />

        <Route
          path="/member/activate-banking"
          component={RouteActivateBanking}
        />

        <Route
          path="/member/authorize/2fa/email/confirm"
          exact
          component={EmailMagicLinkHandler}
        />
        <Route
          path="/member/authorize/verification/email/confirm"
          component={CompleteEmailVerification}
        />
        <Route path="/member/verify/email" component={BeginEmailVerification} />
        <Route
          path="/member/verify/phone-number"
          exact
          component={VerifyPhoneNumber}
        />

        <Route
          path="/member/verify/phone-number/existing-account"
          exact
          component={VerifyPhoneNumberExistingAccount}
        />

        <Route path="/member/verify/phone-number/otp" component={VerifyOTP} />

        <Route
          path="/member/onboarding/set-password"
          exact
          component={OnboardingSetPassword}
        />

        <Route
          path="/member/:partner/subscription-plans/"
          component={NextGenSubscriptionPlans}
        />
        <Route
          path="/member/membership-plans"
          component={MembershipPlansWithAddOns}
        />
        <Route
          path="/member/subscription/trial-success"
          component={MembershipTrialSuccess}
        />
        {!isDesktop && (
          <Route
            key="add-new-token"
            exact
            path="/member/settings/tokens/new"
            component={AddNewToken}
          />
        )}
        {!isDesktop && (
          <Route
            key="edit-api-token"
            exact
            path={`/member/settings/tokens/edit/:sessionId`}
            component={EditNewToken}
          />
        )}

        <Route
          path="*"
          children={
            <BaseAppLayout>
              <Switch>
                <Route path="/member/statements" component={StatementIndex} />
                <Route
                  path="/member/invoices/payouts"
                  component={PayoutsIndex}
                />

                {/* Nav page routes */}
                <Route path="/member/dashboard" component={DashboardIndex} />

                <Route path={PATH_TAXES} component={Taxes} />
                <Route path="/member/tax-documents" component={TaxDocuments} />
                <Route path={PATH_MY_RESOURCES} component={MyResourcesIndex} />

                <Route path="/member/invoices" component={Invoices} />
                <Route
                  path={["/member/imports", "/member/data/batch-imports"]}
                  component={BulkImportsIndex}
                />
                <Route path="/member/data" component={Data} />
                <Route path="/member/1099-filing" component={NEC1099Filing} />
                <Route path="/member/tax-filing" component={Tax1099Filing} />
                <Route path="/member/payees" component={PayeesIndex} />
                <Route
                  path={ENGAGEMENT_TYPES_ROOT_PATH}
                  component={RouteIndexEngagementTypes}
                />
                <Route
                  path={ENGAGEMENT_REQUIREMENTS_ROOT_PATH}
                  component={EngagementRequirementsIndex}
                />

                <Route path="/member/payers" component={PayersIndex} />
                <Route path="/member/bulk-review" component={BulkReviewIndex} />
                <Route path="/member/benefits" component={BenefitsRoutes} />

                <Route path="/member/wallet" component={Wallet} />
                <Route
                  path="/member/organization"
                  component={OrganizationIndex}
                />
                <Route
                  path="/member/bookkeeping"
                  component={BookkeepingRoutes}
                />
                <Redirect
                  exact
                  from="/member/documents"
                  to="/member/my-resources/general-documents"
                />
                <Route
                  path="/member/knowledge-center"
                  component={KnowledgeCenterRoot}
                />
                <Route path="/member/settings" component={Settings} />
                <Route
                  path="/member/personal-pay-link"
                  component={PersonalPayLinkRoutes}
                />
                <Route
                  path="/member/membership-billing-history"
                  component={MembershipBillingHistory}
                />
                <Route
                  path="/member/payments-security"
                  component={PaymentsSecurityScreen}
                />
                <Route path="/member/reports" component={ReportsIndex} />
                <Route path={PATH_TRANSFER} component={Transfer} />

                <Route path={PATH_WORK_LOGS} component={WorkLogs} />

                <Route path="/member/admin" component={Admin} />
                <Redirect from="*" to="/member/dashboard" />
              </Switch>
            </BaseAppLayout>
          }
        />
      </Switch>
    </React.Fragment>
  );
};
